import { useCallback, useState } from 'react'
import { useFormikContext, getIn, FieldArray } from 'formik'
import {
    EquipmentListTagWrapper,
    EquipmentListWrapper,
    EquipmentSelectWrapper,
} from './style'
import { EquipmentListItem } from './EquipmentListItem'

import { Input } from 'shared/components/Input'
import { Icon } from 'shared/components/Icons'
import { Button } from 'shared/components/Button'
import { AreaNomenclature } from 'features/Areas/model/initialValues'
import { NomenclaturesAutocomplete, NomenclaturesOption } from 'features/Areas/ui/NomenclaturesAutocomplete'

interface EquipmentListProps {
    isEditable: boolean
}

export function EquipmentList({ isEditable }: EquipmentListProps) {
    const [equipmentCount, setEquipmentCount] = useState<number>(0)
    const [selectedEquipment, setSelectedEquipment] =
        useState<NomenclaturesOption>()

    const { values, setFieldValue } = useFormikContext<any>()

    const equipments = getIn(values, 'equipment') as AreaNomenclature[]

    const handleAddEquipmentCount = useCallback(() => {
        setEquipmentCount(prev => prev + 1)
    }, [equipmentCount])

    const handleMinusEquipmentCount = useCallback(() => {
        setEquipmentCount(prev => prev - 1)
    }, [equipmentCount])

    function handleOnChangeEquipmentCount(
        e: React.ChangeEvent<HTMLInputElement>,
    ) {
        if (Number.isNaN(Number(e.target.value))) {
            return
        }
        setEquipmentCount(Number(e.target.value))
    }

    function handleOnChangeEquipmentSelect(option: NomenclaturesOption) {
        setSelectedEquipment(option)

        if (!option || !option.value) {
            setEquipmentCount(0)
            return
        }

        const existingEquipment = equipments.find(
            item => item.nomenclature_id === Number(option.value),
        )

        if (!existingEquipment) return

        const currentEquipmentCount = existingEquipment.count

        setEquipmentCount(currentEquipmentCount)
    }

    function handleAddNewEquipment(push: <X = any>(obj: X) => void) {
        if (equipmentCount < 1) return
        if (!selectedEquipment || !selectedEquipment.value) return

        const selectedId = Number(selectedEquipment.value)
        const existingEquipments = equipments.find(
            item => item.nomenclature_id === selectedId,
        )

        if (existingEquipments) {
            const updated = equipments.map((item) =>
                item.nomenclature_id === existingEquipments.nomenclature_id
                    ? { ...item, count: item.count + equipmentCount }
                    : item,
            )
            setFieldValue('equipment', updated)
            return
        }

        push({
            name: selectedEquipment.payload!.name,
            price: selectedEquipment.payload!.price,
            nomenclature_id: selectedId,
            count: equipmentCount,
        })
    }

    return (
        <FieldArray name='equipment'>
            {({ remove, push }) => {
                return (
                    <EquipmentListWrapper>
                        {isEditable && (
                            <EquipmentSelectWrapper>
                                <NomenclaturesAutocomplete
                                    handleOnChangeEquipmentSelect={
                                        handleOnChangeEquipmentSelect
                                    }
                                />
                                <Input
                                    value={equipmentCount}
                                    onChange={handleOnChangeEquipmentCount}
                                    type='number'
                                    $beforeIcon={
                                        <Icon
                                            name='minus'
                                            as='button'
                                            type='button'
                                            onClick={handleMinusEquipmentCount}
                                        />
                                    }
                                    $afterIcon={
                                        <Icon
                                            name='mediumPlus'
                                            as='button'
                                            type='button'
                                            onClick={handleAddEquipmentCount}
                                        />
                                    }
                                />
                                <Button
                                    type='button'
                                    onClick={() => handleAddNewEquipment(push)}
                                >
                                    <Icon name='cocoLineAdd' />
                                </Button>
                            </EquipmentSelectWrapper>
                        )}
                        <EquipmentListTagWrapper>
                            {equipments.map((equipment, index) => (
                                <EquipmentListItem
                                    isEditable={isEditable}
                                    key={index}
                                    index={index}
                                    equipment={equipment}
                                    remove={remove}
                                />
                            ))}
                        </EquipmentListTagWrapper>
                    </EquipmentListWrapper>
                )
            }}
        </FieldArray>
    )
}
